import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { GameLobby } from '../components/GameLobby'
import { sounds } from '../utils/sounds'
import { UserProfileManager } from '../utils/userProfile'
import '../styles/GamePage.css'
import '../styles/Pictionary.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'
import { GameRoomSidebar, GameRoomHeader } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom'
import { useSidebar } from '../hooks/useSidebar'

type Phase = 'setup' | 'lobby' | 'word_select' | 'drawing' | 'reveal' | 'finished'

interface GameState {
  phase: Phase
  round: number
  currentDrawer: number
  word?: string
  wordOptions?: string[]
  guesses: Array<{ playerId: number; playerName: string; guess: string; correct: boolean }>
  scores: Record<number, number>
  totalRounds: number
  totalPlayers: number
  timeLeft: number
  canvasData?: string // Base64 encoded canvas
}

const WORD_LISTS = {
  easy: ['cat', 'dog', 'house', 'tree', 'car', 'sun', 'moon', 'star', 'fish', 'bird', 'book', 'chair', 'cup', 'ball', 'flower'],
  medium: ['pizza', 'guitar', 'camera', 'laptop', 'umbrella', 'bicycle', 'football', 'glasses', 'backpack', 'lighthouse', 'rainbow', 'mountain', 'rocket', 'castle', 'bridge'],
  hard: ['astronaut', 'telescope', 'volcano', 'parachute', 'hammock', 'cactus', 'tornado', 'skateboard', 'peacock', 'pyramid', 'microscope', 'submarine', 'kangaroo', 'saxophone', 'dinosaur'],
}

const ALL_WORDS = [...WORD_LISTS.easy, ...WORD_LISTS.medium, ...WORD_LISTS.hard]

export default function Pictionary() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoJoinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)
  const [numPlayers, setNumPlayers] = useState(2)
  const [rounds, setRounds] = useState(3)
  const [lobbyKey, setLobbyKey] = useState(0)

  const enhancedSession = useEnhancedGameSession({
    sessionCode: undefined,
    playerNumber,
    playerName,
  })

  const { session, createSession, joinSession, voice,  sendMove, onMessage } = enhancedSession

  const [gameState, setGameState] = useState<GameState>({
    phase: 'setup',
    round: 1,
    currentDrawer: 1,
    guesses: [],
    scores: {},
    totalRounds: 3,
    totalPlayers: 2,
    timeLeft: 60,
  })

  const [currentGuess, setCurrentGuess] = useState('')

  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [color, setColor] = useState('#000000')
  const [brushSize, setBrushSize] = useState(3)

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  // Auto-start game when player 2 joins (host only)
  useEffect(() => {
    if (playerNumber === 1 && gameState.phase === 'lobby' && session?.player2_name) {
      startGame()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.player2_name])

  // Sync game state from host
  useEffect(() => {
    const unsubscribe = onMessage('game_state', (data) => {
      console.log('[Pictionary] Received game state:', data)
      setGameState(data.state)

      // Update canvas if we're not the drawer
      if (data.state.canvasData && data.state.currentDrawer !== playerNumber) {
        const canvas = canvasRef.current
        if (canvas) {
          const ctx = canvas.getContext('2d')
          if (ctx) {
            const img = new Image()
            img.onload = () => {
              ctx.clearRect(0, 0, canvas.width, canvas.height)
              ctx.drawImage(img, 0, 0)
            }
            img.src = data.state.canvasData
          }
        }
      }
    })

    return () => unsubscribe()
  }, [onMessage, playerNumber])

  // Handle chat messages
  useEffect(() => {
    const unsubscribeChat = onMessage('chat', (data) => {
      console.log('[Pictionary] Chat message:', data.message)
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribeChat()
    }
  }, [onMessage, playerNumber])

  // Handler for sidebar message sending
  const handleSidebarMessage = useCallback(async (message: string) => {
    await sendMove({ type: 'chat', message })
  }, [sendMove])

  // Timer countdown (host only)
  useEffect(() => {
    if (playerNumber !== 1) return
    if (gameState.phase !== 'drawing') return
    if (gameState.timeLeft <= 0) return

    const interval = setInterval(() => {
      const newTimeLeft = gameState.timeLeft - 1

      if (newTimeLeft <= 0) {
        endRound()
      } else {
        const newState = { ...gameState, timeLeft: newTimeLeft }
        setGameState(newState)
        sendMove({ type: 'game_state', state: newState })
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [gameState, playerNumber, sendMove])

  const getRandomWords = (count: number): string[] => {
    const shuffled = [...ALL_WORDS].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, count)
  }

  // Start game
  const startGame = () => {
    if (playerNumber !== 1) {
      alert('Only the host can start the game!')
      return
    }

    const newState: GameState = {
      phase: 'word_select',
      round: 1,
      currentDrawer: 1,
      wordOptions: getRandomWords(3),
      guesses: [],
      scores: {},
      totalRounds: rounds,
      totalPlayers: numPlayers,
      timeLeft: 60,
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.success()
  }

  // Select word to draw
  const selectWord = (word: string) => {
    if (playerNumber !== gameState.currentDrawer) return

    const newState: GameState = {
      ...gameState,
      word,
      wordOptions: undefined,
      phase: 'drawing',
      timeLeft: 60,
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    setHistoryItems(prev => [...prev, { type: 'event', content: `Round ${gameState.round}: Player ${gameState.currentDrawer} is drawing`, timestamp: Date.now() }])
    sounds.click()
  }

  // Canvas drawing with touch support
  const getPointerPos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()

    if ('touches' in e) {
      // Touch event
      const touch = e.touches[0] || e.changedTouches[0]
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      }
    } else {
      // Mouse event
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }
    }
  }

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (gameState.currentDrawer !== playerNumber) return
    if (gameState.phase !== 'drawing') return

    e.preventDefault()
    setIsDrawing(true)
    draw(e, true)
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>, isStart = false) => {
    if (!isDrawing && !isStart) return
    if (gameState.currentDrawer !== playerNumber) return
    if (gameState.phase !== 'drawing') return

    e.preventDefault()

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const pos = getPointerPos(e)

    ctx.strokeStyle = color
    ctx.lineWidth = brushSize
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    if (isStart) {
      ctx.beginPath()
      ctx.moveTo(pos.x, pos.y)
    } else {
      ctx.lineTo(pos.x, pos.y)
      ctx.stroke()
    }

    // Throttle canvas updates (send every 100ms max)
    if (!(window as any).canvasThrottle) {
      (window as any).canvasThrottle = true
      setTimeout(() => {
        const canvasData = canvas.toDataURL()
        const newState = { ...gameState, canvasData }
        setGameState(newState)
        sendMove({ type: 'game_state', state: newState })
        ;(window as any).canvasThrottle = false
      }, 100)
    }
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const newState = { ...gameState, canvasData: '' }
    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.click()
  }

  // Submit guess
  const submitGuess = () => {
    if (!currentGuess.trim()) return
    if (gameState.currentDrawer === playerNumber) return
    if (gameState.phase !== 'drawing') return

    const correct = currentGuess.toLowerCase().trim() === gameState.word?.toLowerCase()

    const newGuesses = [
      ...gameState.guesses,
      { playerId: playerNumber, playerName, guess: currentGuess, correct },
    ]

    if (correct) {
      // Award points
      const newScores = { ...gameState.scores }
      newScores[playerNumber] = (newScores[playerNumber] || 0) + 10
      newScores[gameState.currentDrawer] = (newScores[gameState.currentDrawer] || 0) + 5

      const newState: GameState = {
        ...gameState,
        guesses: newGuesses,
        scores: newScores,
        phase: 'reveal',
      }

      setGameState(newState)
      sendMove({ type: 'game_state', state: newState })
      setHistoryItems(prev => [...prev, { type: 'event', content: `${playerName} guessed "${gameState.word}" correctly! +10 pts`, timestamp: Date.now() }])
      sounds.success()

      // Auto-advance after 3 seconds
      if (playerNumber === 1) {
        setTimeout(() => nextRound(), 3000)
      }
    } else {
      const newState = { ...gameState, guesses: newGuesses }
      setGameState(newState)
      sendMove({ type: 'game_state', state: newState })
    }

    setCurrentGuess('')
    sounds.click()
  }

  const endRound = () => {
    if (playerNumber !== 1) return

    const newState: GameState = {
      ...gameState,
      phase: 'reveal',
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })

    // Auto-advance after 5 seconds
    setTimeout(() => nextRound(), 5000)
  }

  const nextRound = () => {
    if (playerNumber !== 1) return

    const nextDrawer = (gameState.currentDrawer % gameState.totalPlayers) + 1
    const nextRoundNum = gameState.round + 1

    if (nextRoundNum > gameState.totalRounds) {
      // Game finished
      const newState: GameState = {
        ...gameState,
        phase: 'finished',
      }
      setGameState(newState)
      sendMove({ type: 'game_state', state: newState })
      return
    }

    clearCanvas()

    const newState: GameState = {
      phase: 'word_select',
      round: nextRoundNum,
      currentDrawer: nextDrawer,
      wordOptions: getRandomWords(3),
      guesses: [],
      scores: gameState.scores,
      totalRounds: gameState.totalRounds,
      totalPlayers: gameState.totalPlayers,
      timeLeft: 60,
      canvasData: '',
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
  }

  // Pre-game lobby (setup / waiting for players)
  if (gameState.phase === 'setup' || gameState.phase === 'lobby') {
    // In lobby phase (after creating/joining), show waiting room via GameLobby
    // In setup phase, show the full game lobby UI
    return (
      <GameLobby
        key={lobbyKey}
        gameTitle="Pictionary"
        gameIcon="🎨"
        gameDescription="Draw it, guess it — race against the clock"
        gameBanner="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
        supportedModes={['network']}
        initialJoinCode={autoJoinCode || ''}
        networkLoading={enhancedSession.loading}
        networkError={enhancedSession.error}
        sessionCode={gameState.phase === 'lobby' ? session?.session_code : undefined}
        onBack={() => navigate('/')}
        onStartLocal={() => {}}
        onStartAI={() => {}}
        onCreateNetwork={async (name) => {
          setPlayerName(name)
          await createSession('pictionary', name)
          setPlayerNumber(1)
          setGameState(s => ({ ...s, phase: 'lobby', totalRounds: rounds, totalPlayers: numPlayers }))
        }}
        onJoinNetwork={async (name, code) => {
          setPlayerName(name)
          await joinSession(code, name)
          setPlayerNumber(2)
          setGameState(s => ({ ...s, phase: 'lobby' }))
        }}
        onCancelWaiting={() => {
          setLobbyKey(k => k + 1)
          setGameState(s => ({ ...s, phase: 'setup' }))
        }}
        extraSetupContent={(mode) => mode === 'network' ? (
          <div className="gl-field">
            <label className="gl-label">Game Settings</label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label className="gl-label" style={{ marginBottom: '6px', display: 'block' }}>Players</label>
                <select
                  className="gl-input"
                  value={numPlayers}
                  onChange={e => setNumPlayers(Number(e.target.value))}
                  style={{ cursor: 'pointer' }}
                >
                  {[2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n} style={{ background: '#1a1a2e' }}>{n} Players</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="gl-label" style={{ marginBottom: '6px', display: 'block' }}>Rounds</label>
                <select
                  className="gl-input"
                  value={rounds}
                  onChange={e => setRounds(Number(e.target.value))}
                  style={{ cursor: 'pointer' }}
                >
                  {[1, 2, 3, 5, 7, 10].map(n => (
                    <option key={n} value={n} style={{ background: '#1a1a2e' }}>{n} Round{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ) : null}
      />
    )
  }

  // Main game
  const isDrawer = gameState.currentDrawer === playerNumber
  const sortedScores = Object.entries(gameState.scores).sort((a, b) => b[1] - a[1])

  return (
    <div className="pictionary-container universal-game-room">
      <GameRoomHeader
        gameTitle="Pictionary"
        gameIcon="🎨"
        mode="network"
        players={Array.from({ length: gameState.totalPlayers }, (_, i) => ({
          name: i === 0 ? (session?.player1_name || 'Player 1') : i === 1 ? (session?.player2_name || `Player ${i + 1}`) : `Player ${i + 1}`,
          status: (gameState.currentDrawer === i + 1 ? 'thinking' : 'active') as 'active' | 'thinking',
        }))}
        sessionCode={session?.session_code}
        onBack={() => navigate('/')}
      />

      <div className="game-info" style={{ marginTop: '60px', padding: 'var(--space-md)', textAlign: 'center' }}>
        <span>Round {gameState.round}/{gameState.totalRounds}</span>
        <span className="timer" style={{ marginLeft: '1rem' }}>{gameState.timeLeft}s</span>
        {voice.isConnected && <span className="webrtc-badge" style={{ marginLeft: '1rem' }}>⚡ Real-time</span>}
      </div>

      {gameState.phase === 'finished' && (
        <div className="winner-overlay">
          <h2>🎉 Game Finished!</h2>
          <div className="final-scores">
            <h3>Final Scores</h3>
            {sortedScores.map(([playerId, score]) => (
              <div key={playerId} className="score-entry">
                Player {playerId}: {score} points
              </div>
            ))}
            {sortedScores.length > 0 && (
              <div className="winner-announce">
                🏆 Winner: Player {sortedScores[0][0]} with {sortedScores[0][1]} points!
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main content area with game board and sidebar */}
      <div className="game-room-content">
        {/* Game Board */}
        <div className="game-board-area">
        {/* Word selection */}
        {gameState.phase === 'word_select' && isDrawer && gameState.wordOptions && (
          <div className="word-selection">
            <h3>Choose a word to draw:</h3>
            <div className="word-options">
              {gameState.wordOptions.map(word => (
                <button key={word} onClick={() => selectWord(word)} className="word-button">
                  {word}
                </button>
              ))}
            </div>
          </div>
        )}

        {gameState.phase === 'word_select' && !isDrawer && (
          <div className="waiting-message">
            <h3>Player {gameState.currentDrawer} is choosing a word...</h3>
          </div>
        )}

        {/* Drawing area */}
        {(gameState.phase === 'drawing' || gameState.phase === 'reveal') && (
          <div className="canvas-area">
            <div className="word-display">
              {isDrawer || gameState.phase === 'reveal' ? (
                <h2>Word: {gameState.word}</h2>
              ) : (
                <h2>Guess the drawing!</h2>
              )}
            </div>

            {isDrawer && gameState.phase === 'drawing' && (
              <div className="drawing-tools">
                <label>
                  Color:
                  <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
                </label>
                <label>
                  Size:
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={brushSize}
                    onChange={(e) => setBrushSize(Number(e.target.value))}
                  />
                  <span>{brushSize}px</span>
                </label>
                <button onClick={clearCanvas} className="clear-button">Clear Canvas</button>
              </div>
            )}

            <canvas
              ref={canvasRef}
              width={800}
              height={600}
              className="drawing-canvas"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        )}

        {/* Guessing area */}
        {gameState.phase === 'drawing' && !isDrawer && (
          <div className="guess-area">
            <input
              type="text"
              placeholder="Type your guess..."
              value={currentGuess}
              onChange={(e) => setCurrentGuess(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && submitGuess()}
              autoFocus
            />
            <button onClick={submitGuess} disabled={!currentGuess.trim()}>
              Submit Guess
            </button>
          </div>
        )}

        {/* Guesses list */}
        {gameState.guesses.length > 0 && (
          <div className="guesses-list">
            <h3>Guesses</h3>
            <div className="guesses-scroll">
              {gameState.guesses.map((g, i) => (
                <div key={i} className={`guess-entry ${g.correct ? 'correct' : ''}`}>
                  <strong>{g.playerName}:</strong> {g.guess}
                  {g.correct && ' ✓ CORRECT!'}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scoreboard */}
        <div className="scoreboard">
          <h3>Scores</h3>
          {sortedScores.length > 0 ? (
            sortedScores.map(([playerId, score]) => (
              <div key={playerId} className="score-entry">
                <span>Player {playerId}</span>
                <span>{score} pts</span>
              </div>
            ))
          ) : (
            <p>No scores yet</p>
          )}
        </div>

        <div className="game-rules">
          <details>
            <summary>📖 How to Play</summary>
            <ul>
              <li>One player draws a word while others guess</li>
              <li>Drawer chooses from 3 random words</li>
              <li>Type your guesses - first correct guess wins!</li>
              <li>Correct guess: +10 points for guesser, +5 for drawer</li>
              <li>60 seconds per round</li>
              <li>Everyone gets a turn to draw</li>
              <li>Most points at the end wins!</li>
            </ul>
          </details>
        </div>
      </div>
      </div>

      {/* Universal Sidebar with bottom-sheet mode on mobile */}
      <GameRoomSidebar
        mode="network"
        gameState={{
          turn: gameState.phase === 'drawing' ? `Player ${gameState.currentDrawer} drawing` : 'Waiting',
          phase: gameState.phase === 'finished' ? 'Game Over' : 'Playing',
          score: sortedScores.length > 0 ? Object.fromEntries(
            sortedScores.map(([id, score]) => [`Player ${id}`, `${score} pts`])
          ) : {}
        }}
        messages={chatMessages}
        historyItems={historyItems}
        onSendMessage={handleSidebarMessage}
        collapsed={sidebar.collapsed}
        onToggleCollapse={sidebar.toggle}
        position={sidebar.position}
        quickReactions={true}
      />
    </div>
  )
}
